import type { Asset } from '../types';
import { extractPosterFrame } from './videoPoster';

const DB_NAME = 'loopcase-assets';
const DB_VERSION = 1;
const STORE_NAME = 'blobs';

interface CachedRecord {
  id: string;
  blob: Blob;
  type: 'image' | 'video';
  focalX: number;
  focalY: number;
  zoom: number;
  visible: boolean;
  posterBlob?: Blob;
  skippedCache?: boolean;
  staticUrl?: string;     // for non-blob URLs (placeholder images, etc.)
  duration?: number;
  label?: string;
  tag?: string;
  cachedAt: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Cache an asset's blob (and optional poster blob) to IndexedDB.
 *  For large videos, checks quota and skips if the blob would use >60% of remaining space.
 *  For static-URL assets (non-blob), stores just the URL without fetching a blob. */
export async function cacheAsset(asset: Asset, blob?: Blob, posterBlob?: Blob): Promise<void> {
  try {
    const isStaticUrl = !asset.url.startsWith('blob:');

    // Static URL assets — store just the URL, no blob needed
    if (isStaticUrl) {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put({
        id: asset.id,
        blob: new Blob([]),
        type: asset.type ?? 'image',
        focalX: asset.focalX ?? 0.5,
        focalY: asset.focalY ?? 0.5,
        zoom: asset.zoom ?? 1,
        visible: asset.visible !== false,
        staticUrl: asset.url,
        duration: asset.duration,
        label: asset.label,
        tag: asset.tag,
        cachedAt: Date.now(),
      } satisfies CachedRecord);
      return;
    }

    if (!blob) return;

    // Quota check for large blobs (videos)
    if (blob.size > 10 * 1024 * 1024 && navigator.storage?.estimate) {
      const est = await navigator.storage.estimate();
      const remaining = (est.quota ?? 0) - (est.usage ?? 0);
      if (blob.size > remaining * 0.6) {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put({
          id: asset.id,
          blob: new Blob([]),
          type: asset.type ?? 'image',
          focalX: asset.focalX ?? 0.5,
          focalY: asset.focalY ?? 0.5,
          zoom: asset.zoom ?? 1,
          visible: asset.visible !== false,
          skippedCache: true,
          duration: asset.duration,
          label: asset.label,
          tag: asset.tag,
          cachedAt: Date.now(),
        } satisfies CachedRecord);
        return;
      }
    }

    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put({
      id: asset.id,
      blob,
      type: asset.type ?? 'image',
      focalX: asset.focalX ?? 0.5,
      focalY: asset.focalY ?? 0.5,
      zoom: asset.zoom ?? 1,
      visible: asset.visible !== false,
      posterBlob,
      duration: asset.duration,
      label: asset.label,
      tag: asset.tag,
      cachedAt: Date.now(),
    } satisfies CachedRecord);
  } catch {
    // IndexedDB may be unavailable — silently skip
  }
}

/** Restore all cached assets from IndexedDB, recreating blob URLs. */
export async function loadCachedAssets(): Promise<Asset[]> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);

    const records: CachedRecord[] = await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });

    if (records.length === 0) return [];

    const assets: Asset[] = [];
    for (const rec of records) {
      // Static URL asset — use the stored URL directly (no blob needed)
      if (rec.staticUrl) {
        // Migrate stale .png placeholder URLs to .webp
        const url = rec.staticUrl.startsWith('/cards/') && rec.staticUrl.endsWith('.png')
          ? rec.staticUrl.replace(/\.png$/, '.webp')
          : rec.staticUrl;
        assets.push({
          id: rec.id,
          url,
          type: rec.type,
          focalX: rec.focalX,
          focalY: rec.focalY,
          zoom: rec.zoom,
          visible: rec.visible,
          duration: rec.duration,
          label: rec.label,
          tag: rec.tag,
        });
        continue;
      }

      if (rec.skippedCache || rec.blob.size === 0) continue;

      const url = URL.createObjectURL(rec.blob);
      let posterUrl: string | undefined;
      if (rec.posterBlob && rec.posterBlob.size > 0) {
        posterUrl = URL.createObjectURL(rec.posterBlob);
      } else if (rec.type === 'video') {
        try { posterUrl = await extractPosterFrame(url); } catch { /* best-effort */ }
      }

      assets.push({
        id: rec.id,
        url,
        type: rec.type,
        focalX: rec.focalX,
        focalY: rec.focalY,
        zoom: rec.zoom,
        visible: rec.visible,
        posterUrl,
        duration: rec.duration,
        label: rec.label,
        tag: rec.tag,
      });
    }
    return assets;
  } catch {
    return [];
  }
}

/** Remove a single cached asset by ID. */
export async function removeCachedAsset(id: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
  } catch { /* silent */ }
}

/** Clear all cached assets. */
export async function clearCachedAssets(): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
  } catch { /* silent */ }
}
